'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { t } from '@/data/translations';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  langCode: string;
}

export default function HelpModal({ isOpen, onClose, langCode }: HelpModalProps) {
  const items: { icon: string; key: 'helpTap' | 'helpVote' | 'helpWrite' | 'helpLanguage' }[] = [
    { icon: '👆', key: 'helpTap' },
    { icon: '👍', key: 'helpVote' },
    { icon: '✍️', key: 'helpWrite' },
    { icon: '🌐', key: 'helpLanguage' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-slate-900 rounded-3xl border border-slate-700/50 w-full max-w-md p-6 pointer-events-auto"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-bold text-xl" lang={langCode}>{t('helpTitle', langCode)}</h2>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="text-slate-500 hover:text-white p-1 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Intro */}
              <p className="text-slate-400 text-sm mb-5 leading-relaxed" lang={langCode}>
                {t('helpIntro', langCode)}
              </p>

              {/* Items */}
              <ul className="space-y-3">
                {items.map(({ icon, key }) => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="text-2xl shrink-0 leading-none pt-0.5">{icon}</span>
                    <span className="text-slate-200 text-sm leading-snug pt-1" lang={langCode}>
                      {t(key, langCode)}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Brand footer */}
              <div className="mt-6 pt-4 border-t border-slate-800 text-center text-xs text-slate-600">
                <span className="text-sky-400/80">Earth</span>
                <span className="mx-1">to</span>
                <span className="text-slate-400">America</span>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
